import React, { useState, useRef } from "react";
import { BsSearch } from "react-icons/bs";
import axios from "axios";
import { toast } from "react-toastify";

const API = "https://eps.elkomprintama.com/";
const MAX_SIZE = 2 * 1024 * 1024;

const LIST_UNIT = ["mm", "inch"];
const LIST_RADIUS = [
  "-",
  "BO",
  "R0.5",
  "R1",
  "R2",
  "R2.5",
  "R3",
  "R3.5",
  "R4",
  "R4.5",
  "R5",
  "R6",
];
const LIST_MODEL = ["DIAMETER", "OVAL", "INNER", "ARROW", "SPECIAL", "STAR"];

function formatNum(num) {
  if (num === null || num === undefined) return "—";
  return parseFloat(num).toString();
}

function EditModal({ die, type, onClose, onSaved }) {
  const isNormal = type === "normal";
  const fileInputRef = useRef(null);

  const [fields, setFields] = useState(
    isNormal
      ? {
          die_p: die.die_p,
          die_l: die.die_l,
          die_unit: die.die_unit,
          die_r: die.die_r,
          die_ups: die.die_ups,
        }
      : {
          spec_model: die.spec_model,
          spec_p: die.spec_p,
          spec_l: die.spec_l,
          spec_unit: die.spec_unit,
          spec_ups: die.spec_ups,
        },
  );
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(
    die.image_path
      ? `${API}get/api/diecut/image?path=${encodeURIComponent(die.image_path)}`
      : null,
  );
  const [saving, setSaving] = useState(false);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Only image files allowed.");
      e.target.value = "";
      return;
    }
    if (file.size > MAX_SIZE) {
      toast.error("Image must be under 2MB.");
      e.target.value = "";
      return;
    }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(fields).forEach(([k, v]) => fd.append(k, v));
      if (imageFile) fd.append("image", imageFile);

      const id = isNormal ? die.die_id : die.spec_id;
      await axios.patch(`${API}patch/api/diecut/${type}/${id}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true,
      });
      toast.success("Saved!");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.msg || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  const inp =
    "appearance-none block w-full border bg-white border-gray-300 rounded py-2 px-3 text-sm leading-tight focus:outline-none focus:border-blue-400";
  const sel =
    "block appearance-none w-full border border-gray-300 bg-white text-gray-700 py-2 px-3 pr-8 rounded text-sm leading-tight focus:outline-none focus:border-blue-400";
  const lbl =
    "block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-gray-700 text-white px-5 py-3 flex items-center justify-between">
          <span className="font-bold text-sm uppercase tracking-wide">
            Edit {isNormal ? "Normal" : "Special"} Diecut #
            {isNormal ? die.die_id : die.spec_id}
          </span>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-300 text-xl leading-none font-bold"
          >
            ✕
          </button>
        </div>

        <div className="p-5 space-y-3">
          {isNormal ? (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={lbl}>Length</label>
                <input
                  className={inp}
                  type="text"
                  value={fields.die_p}
                  onChange={(e) =>
                    setFields((f) => ({ ...f, die_p: e.target.value }))
                  }
                />
              </div>
              <div>
                <label className={lbl}>Width</label>
                <input
                  className={inp}
                  type="text"
                  value={fields.die_l}
                  onChange={(e) =>
                    setFields((f) => ({ ...f, die_l: e.target.value }))
                  }
                />
              </div>
              <div>
                <label className={lbl}>Unit</label>
                <select
                  className={sel}
                  value={fields.die_unit}
                  onChange={(e) =>
                    setFields((f) => ({ ...f, die_unit: e.target.value }))
                  }
                >
                  {LIST_UNIT.map((u) => (
                    <option key={u} value={u}>
                      {u}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={lbl}>Radius</label>
                <select
                  className={sel}
                  value={fields.die_r}
                  onChange={(e) =>
                    setFields((f) => ({ ...f, die_r: e.target.value }))
                  }
                >
                  {LIST_RADIUS.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-span-2">
                <label className={lbl}>Ups</label>
                <input
                  className={inp}
                  type="text"
                  value={fields.die_ups}
                  onChange={(e) =>
                    setFields((f) => ({ ...f, die_ups: e.target.value }))
                  }
                />
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className={lbl}>Model</label>
                <select
                  className={sel}
                  value={fields.spec_model}
                  onChange={(e) =>
                    setFields((f) => ({ ...f, spec_model: e.target.value }))
                  }
                >
                  {LIST_MODEL.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={lbl}>Length</label>
                <input
                  className={inp}
                  type="text"
                  value={fields.spec_p}
                  onChange={(e) =>
                    setFields((f) => ({ ...f, spec_p: e.target.value }))
                  }
                />
              </div>
              <div>
                <label className={lbl}>Width</label>
                <input
                  className={inp}
                  type="text"
                  value={fields.spec_l}
                  onChange={(e) =>
                    setFields((f) => ({ ...f, spec_l: e.target.value }))
                  }
                />
              </div>
              <div>
                <label className={lbl}>Unit</label>
                <select
                  className={sel}
                  value={fields.spec_unit}
                  onChange={(e) =>
                    setFields((f) => ({ ...f, spec_unit: e.target.value }))
                  }
                >
                  {LIST_UNIT.map((u) => (
                    <option key={u} value={u}>
                      {u}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={lbl}>Ups</label>
                <input
                  className={inp}
                  type="text"
                  value={fields.spec_ups}
                  onChange={(e) =>
                    setFields((f) => ({ ...f, spec_ups: e.target.value }))
                  }
                />
              </div>
            </div>
          )}

          {/* Image section */}
          <div className="border border-dashed border-gray-200 rounded-lg p-3 bg-gray-50">
            <label className={lbl}>
              Image{" "}
              <span className="normal-case font-normal text-gray-400">
                (max 2MB)
              </span>
            </label>
            <div className="flex items-center gap-3 mt-1">
              <label className="cursor-pointer bg-white hover:bg-gray-100 border border-gray-300 text-gray-700 text-xs font-medium px-3 py-1.5 rounded transition">
                {imagePreview ? "Replace Image" : "Upload Image"}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageChange}
                />
              </label>
              {imageFile && (
                <span className="text-xs text-gray-500 flex items-center gap-1">
                  {imageFile.name}
                  <button
                    type="button"
                    onClick={() => {
                      setImageFile(null);
                      setImagePreview(
                        die.image_path
                          ? `${API}get/api/diecut/image?path=${encodeURIComponent(die.image_path)}`
                          : null,
                      );
                      if (fileInputRef.current) fileInputRef.current.value = "";
                    }}
                    className="text-red-400 hover:text-red-600 font-bold ml-1"
                  >
                    ✕
                  </button>
                </span>
              )}
            </div>
            {imagePreview && (
              <div className="mt-3 flex justify-center bg-white border border-gray-100 rounded p-2">
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="max-h-40 max-w-full object-contain"
                />
              </div>
            )}
          </div>
        </div>

        <div className="px-5 pb-5 flex gap-2 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white font-bold rounded disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Diecut() {
  const [dieCut, setDieCut] = useState([]);
  const [specDieCut, setSpecDieCut] = useState([]);
  const [filterLength, setFilterLength] = useState("");
  const [filterWidth, setFilterWidth] = useState("");
  const [viewType, setViewType] = useState("0");
  const [filteredDieCut, setFilteredDieCut] = useState([]);
  const [filteredSpecDieCut, setFilteredSpecDieCut] = useState([]);
  const [previewSrc, setPreviewSrc] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleTypeChange = (e) => {
    setViewType(e.target.value);
    setDieCut([]);
    setSpecDieCut([]);
    setFilteredDieCut([]);
    setFilteredSpecDieCut([]);
    setFilterLength("");
    setFilterWidth("");
  };

  const handleFilter = async (e) => {
    e.preventDefault();
    if (!filterLength.trim()) {
      toast.warning("Enter a length to search.");
      return;
    }
    setLoading(true);
    try {
      if (viewType === "0") {
        const res = await axios.get(`${API}get/api/diecut/normal`);
        const all = res.data.result;
        setDieCut(all);
        setFilteredDieCut(
          all.filter((d) => {
            const c1 = d.die_p.toFixed(3).startsWith(filterLength);
            const c2 = filterWidth
              ? d.die_l.toFixed(3).startsWith(filterWidth)
              : true;
            return c1 && c2;
          }),
        );
      } else {
        const res = await axios.get(`${API}get/api/diecut/special`);
        const all = res.data.result;
        setSpecDieCut(all);
        setFilteredSpecDieCut(
          all.filter((d) => {
            const c1 = String(d.spec_p).startsWith(filterLength);
            const c2 = filterWidth
              ? String(d.spec_l).startsWith(filterWidth)
              : true;
            return c1 && c2;
          }),
        );
      }
    } catch {
      toast.error("Failed to load.");
    } finally {
      setLoading(false);
    }
  };

  const reloadCurrent = async () => {
    if (viewType === "0" && dieCut.length) {
      const res = await axios.get(`${API}get/api/diecut/normal`);
      setDieCut(res.data.result);
      setFilteredDieCut(
        res.data.result.filter((d) => {
          const c1 = filterLength
            ? d.die_p.toFixed(3).startsWith(filterLength)
            : true;
          const c2 = filterWidth
            ? d.die_l.toFixed(3).startsWith(filterWidth)
            : true;
          return c1 && c2;
        }),
      );
    } else if (viewType === "1" && specDieCut.length) {
      const res = await axios.get(`${API}get/api/diecut/special`);
      setSpecDieCut(res.data.result);
      setFilteredSpecDieCut(
        res.data.result.filter((d) => {
          const c1 = filterLength
            ? String(d.spec_p).startsWith(filterLength)
            : true;
          const c2 = filterWidth
            ? String(d.spec_l).startsWith(filterWidth)
            : true;
          return c1 && c2;
        }),
      );
    }
  };

  const openPreview = (imagePath) => {
    setPreviewSrc(
      `${API}get/api/diecut/image?path=${encodeURIComponent(imagePath)}`,
    );
  };

  const stripe = (i) => (i % 2 === 0 ? "bg-gray-50" : "bg-white");

  const thCls =
    "px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide whitespace-nowrap border-r last:border-r-0";
  const tdCls =
    "px-4 py-2.5 text-xs text-gray-700 border-r last:border-r-0 whitespace-nowrap";

  const currentList = viewType === "0" ? filteredDieCut : filteredSpecDieCut;
  const hasData = currentList.length > 0;

  return (
    <>
      <div
        className="flex flex-col"
        style={{ maxHeight: "calc(100vh - 80px)" }}
      >
        {/* Header */}
        <div className="w-full rounded-lg bg-gray-700 mb-4 flex-shrink-0">
          <label className="block uppercase text-white tracking-wide text-5xl text-center font-bold py-2">
            Diecut List
          </label>
        </div>

        {/* Controls bar */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 flex-shrink-0">
          <div className="flex flex-wrap items-end gap-3">
            {/* Type selector */}
            <div className="min-w-[180px]">
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                Type
              </label>
              <select
                className="block w-full border border-gray-300 text-gray-700 py-2 px-3 pr-8 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-gray-400 bg-white"
                value={viewType}
                onChange={handleTypeChange}
              >
                <option value="0">Normal Diecut</option>
                <option value="1">Special Diecut</option>
              </select>
            </div>

            {/* Filters */}
            <form onSubmit={handleFilter} className="flex items-end gap-2">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                  Length
                </label>
                <input
                  className="border border-gray-300 rounded-lg py-2 px-3 text-sm w-28 focus:outline-none focus:ring-1 focus:ring-gray-400 bg-white"
                  type="text" autoComplete="off"
                  placeholder="e.g. 10"
                  value={filterLength}
                  onChange={(e) => setFilterLength(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                  Width
                </label>
                <input
                  className="border border-gray-300 rounded-lg py-2 px-3 text-sm w-28 focus:outline-none focus:ring-1 focus:ring-gray-400 bg-white"
                  type="text" autoComplete="off"
                  placeholder="e.g. 5"
                  value={filterWidth}
                  onChange={(e) => setFilterWidth(e.target.value)}
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="flex items-center gap-1.5 bg-gray-700 hover:bg-gray-800 text-white text-sm font-semibold px-4 py-2 rounded-lg transition disabled:opacity-50"
              >
                <BsSearch className="w-3.5 h-3.5" />
                {loading ? "Searching…" : "Find"}
              </button>
            </form>

            {hasData && (
              <span className="text-xs text-gray-400 ml-auto self-center">
                {currentList.length} record{currentList.length !== 1 ? "s" : ""}
              </span>
            )}
          </div>
        </div>

        {/* Table — fills remaining height */}
        <div className="flex-1 min-h-0 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden flex flex-col mt-4">
          {!hasData ? (
            <div className="flex-1 flex items-center justify-center text-gray-400 italic text-sm">
              {loading ? "Searching..." : "Enter a length and click Find"}
            </div>
          ) : (
            <div className="flex-1 min-h-0 overflow-auto">
              <table className="w-full text-sm border-collapse">
                {viewType === "0" ? (
                  <>
                    <thead className="bg-gray-700 text-white text-xs sticky top-0">
                      <tr>
                        <th className={thCls}>#</th>
                        <th className={thCls}>Length</th>
                        <th className={thCls}>Width</th>
                        <th className={thCls}>Unit</th>
                        <th className={thCls}>Radius</th>
                        <th className={thCls}>Ups</th>
                        <th className={`${thCls} text-center`}>Image</th>
                        <th className={`${thCls} text-center`}>Edit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredDieCut.map((die, i) => (
                        <tr
                          key={die.die_id}
                          className={`border-t hover:bg-blue-50 transition-colors ${stripe(i)}`}
                        >
                          <td className={`${tdCls} text-gray-400`}>
                            {die.die_id}
                          </td>
                          <td className={`${tdCls} font-medium`}>
                            {formatNum(die.die_p)}
                          </td>
                          <td className={tdCls}>{formatNum(die.die_l)}</td>
                          <td className={tdCls}>{die.die_unit}</td>
                          <td className={tdCls}>{die.die_r}</td>
                          <td className={tdCls}>{die.die_ups}</td>
                          <td className={`${tdCls} text-center`}>
                            {die.image_path ? (
                              <button
                                onClick={() => openPreview(die.image_path)}
                                className="inline-flex items-center gap-1 text-xs bg-blue-50 hover:bg-blue-100 text-blue-600 font-medium px-2.5 py-1 rounded-full transition"
                                title="View image"
                              >
                                🖼️ View
                              </button>
                            ) : (
                              <span className="text-gray-300 text-xs">—</span>
                            )}
                          </td>
                          <td className={`${tdCls} text-center`}>
                            <button
                              onClick={() =>
                                setEditTarget({ die, type: "normal" })
                              }
                              className="inline-flex items-center gap-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium px-2.5 py-1 rounded-full transition"
                            >
                              ✏️ Edit
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </>
                ) : (
                  <>
                    <thead className="bg-gray-700 text-white text-xs sticky top-0">
                      <tr>
                        <th className={thCls}>#</th>
                        <th className={thCls}>Model</th>
                        <th className={thCls}>Length</th>
                        <th className={thCls}>Width</th>
                        <th className={thCls}>Unit</th>
                        <th className={thCls}>Ups</th>
                        <th className={`${thCls} text-center`}>Image</th>
                        <th className={`${thCls} text-center`}>Edit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredSpecDieCut.map((die, i) => (
                        <tr
                          key={die.spec_id}
                          className={`border-t hover:bg-blue-50 transition-colors ${stripe(i)}`}
                        >
                          <td className={`${tdCls} text-gray-400`}>
                            {die.spec_id}
                          </td>
                          <td className={`${tdCls} font-medium`}>
                            {die.spec_model}
                          </td>
                          <td className={tdCls}>{formatNum(die.spec_p)}</td>
                          <td className={tdCls}>{formatNum(die.spec_l)}</td>
                          <td className={tdCls}>{die.spec_unit}</td>
                          <td className={tdCls}>{die.spec_ups}</td>
                          <td className={`${tdCls} text-center`}>
                            {die.image_path ? (
                              <button
                                onClick={() => openPreview(die.image_path)}
                                className="inline-flex items-center gap-1 text-xs bg-blue-50 hover:bg-blue-100 text-blue-600 font-medium px-2.5 py-1 rounded-full transition"
                                title="View image"
                              >
                                🖼️ View
                              </button>
                            ) : (
                              <span className="text-gray-300 text-xs">—</span>
                            )}
                          </td>
                          <td className={`${tdCls} text-center`}>
                            <button
                              onClick={() =>
                                setEditTarget({ die, type: "special" })
                              }
                              className="inline-flex items-center gap-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium px-2.5 py-1 rounded-full transition"
                            >
                              ✏️ Edit
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </>
                )}
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Image preview modal */}
      {previewSrc && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 p-4"
          onClick={() => setPreviewSrc(null)}
        >
          <div
            className="relative bg-white rounded-xl shadow-2xl p-4 max-w-3xl w-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="absolute -top-3 -right-3 bg-white rounded-full w-8 h-8 flex items-center justify-center text-gray-600 hover:text-gray-900 shadow font-bold text-sm"
              onClick={() => setPreviewSrc(null)}
            >
              ✕
            </button>
            <img
              src={previewSrc}
              alt="Diecut"
              style={{ maxHeight: "80vh", maxWidth: "70vw" }}
              className="object-contain rounded block"
            />
          </div>
        </div>
      )}

      {/* Edit modal */}
      {editTarget && (
        <EditModal
          die={editTarget.die}
          type={editTarget.type}
          onClose={() => setEditTarget(null)}
          onSaved={reloadCurrent}
        />
      )}
    </>
  );
}

export default Diecut;
